import React, { useState, useEffect, useCallback } from 'react';
import {
  Table,
  Button,
  Input,
  Select,
  Space,
  Modal,
  Form,
  message,
  Popconfirm,
  Tag,
  Row,
  Col,
  DatePicker,
  Upload,
  ConfigProvider,
} from 'antd';
import type { ColumnsType } from 'antd/es/table';
import dayjs from 'dayjs';
import 'dayjs/locale/zh-cn';
import zhCN from 'antd/locale/zh_CN';
import {
  PlusOutlined,
  EditOutlined,
  DeleteOutlined,
  DownloadOutlined,
  UploadOutlined,
  FileExcelOutlined,
} from '@ant-design/icons';
import { policyApi, PolicyItem, PolicyListParams } from '../../../../../services/policyService';
import { useDict } from '../../../../../hooks/useDict';
import QueryArea, { QueryField } from '../../components/QueryArea';
import { PAGINATION_CONFIG } from '../../components/PaginationConfig';
import './Policy.css';

// 设置dayjs默认语言为中文
dayjs.locale('zh-cn');

const { Option } = Select;
const { TextArea } = Input;
const { RangePicker } = DatePicker;

const PolicyManagement: React.FC = () => {
  const [loading, setLoading] = useState(false);
  const [dataSource, setDataSource] = useState<PolicyItem[]>([]);
  const [pagination, setPagination] = useState({
    current: 1,
    pageSize: 10,
    total: 0,
  });
  const [searchParams, setSearchParams] = useState<PolicyListParams>({});
  const [modalVisible, setModalVisible] = useState(false);
  const [editingItem, setEditingItem] = useState<PolicyItem | null>(null);
  const [selectedRowKeys, setSelectedRowKeys] = useState<React.Key[]>([]);
  const [form] = Form.useForm();

  // 数据字典
  const { dictData: policyBusinessCategoryDict, loading: dictLoading, error: dictError } = useDict('policy_business_category');
  
  // 调试字典数据
  useEffect(() => {
  }, [policyBusinessCategoryDict, dictLoading, dictError]);

  // 查询字段配置
  const queryFields: QueryField[] = [
    {
      key: 'fileName',
      label: '文件名',
      type: 'input',
      placeholder: '请输入文件名或内容',
      width: 300,
    },
    {
      key: 'businessCategory',
      label: '业务类型',
      type: 'dict',
      dictType: 'policy_business_category',
      placeholder: '请选择业务类型',
      width: 200,
    },
    {
      key: 'documentNumber',
      label: '文件号',
      type: 'input',
      placeholder: '请输入文件号',
      width: 200,
    },
    {
      key: 'dateRange',
      label: '印发时间',
      type: 'dateRange',
      placeholder: '请选择时间范围',
      width: 300,
    },
  ];

  // 加载数据
  const loadData = useCallback(async (params?: PolicyListParams) => {
    setLoading(true);
    try {
      const requestParams = {
        current: pagination.current,
        size: pagination.pageSize,
        ...searchParams,
        ...params,
      };

      // 处理日期范围
      if (requestParams.dateRange && Array.isArray(requestParams.dateRange) && requestParams.dateRange.length === 2) {
        requestParams.startDate = requestParams.dateRange[0];
        requestParams.endDate = requestParams.dateRange[1];
        delete requestParams.dateRange;
      }

      // 处理文件名参数映射
      if (requestParams.fileName) {
        requestParams.keyword = requestParams.fileName;
        delete requestParams.fileName;
      }

      const response = await policyApi.getList(requestParams);
      setDataSource(response.data.records);
      setPagination(prev => ({
        ...prev,
        total: response.data.total,
        current: response.data.current,
      }));
    } catch (error) {
      message.error('加载数据失败');
    } finally {
      setLoading(false);
    }
  }, [pagination.current, pagination.pageSize, searchParams]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  // 搜索参数变化处理
  const handleSearchParamsChange = (values: any) => {
    setSearchParams(values);
  };

  // 搜索
  const handleSearch = () => {
    setPagination(prev => ({ ...prev, current: 1 }));
    loadData(searchParams);
  };

  // 重置
  const handleReset = () => {
    setSearchParams({});
    setPagination(prev => ({ ...prev, current: 1 }));
    loadData({});
  };

  // 新增
  const handleAdd = () => {
    setEditingItem(null);
    form.resetFields();
    setModalVisible(true);
  };

  // 编辑
  const handleEdit = (record: PolicyItem) => {
    setEditingItem(record);
    form.setFieldsValue({
      ...record,
      issuanceDate: record.issuanceDate ? dayjs(record.issuanceDate) : null,
    });
    setModalVisible(true);
  };

  // 删除
  const handleDelete = async (id: number) => {
    try {
      await policyApi.delete(id);
      message.success('删除成功');
      loadData();
    } catch (error) {
      message.error('删除失败');
    }
  };

  // 批量删除
  const handleBatchDelete = async () => {
    if (selectedRowKeys.length === 0) {
      message.warning('请先选择要删除的数据');
      return;
    }
    
    try {
      await policyApi.batchDelete(selectedRowKeys as number[]);
      message.success('批量删除成功');
      setSelectedRowKeys([]);
      loadData();
    } catch (error) {
      message.error('批量删除失败');
    }
  };

  // 导出
  const handleExport = async () => {
    try {
      const requestParams = { ...searchParams };
      if (requestParams.dateRange && requestParams.dateRange.length === 2) {
        requestParams.startDate = requestParams.dateRange[0];
        requestParams.endDate = requestParams.dateRange[1];
        delete requestParams.dateRange;
      }


      const response = await policyApi.export(requestParams);

      // 检查响应数据
      if (!response.data) {
        throw new Error('响应数据为空');
      }

      // 直接使用response.data作为Blob
      const blob = response.data;
      

      if (blob.size === 0) {
        throw new Error('生成的Excel文件为空');
      }
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `政策导航数据_${new Date().toISOString().slice(0, 10)}.xlsx`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);

      message.success('导出成功');
    } catch (error) {
      console.error('导出失败:', error);
      const errorMessage = error instanceof Error ? error.message : String(error);
      message.error('导出失败: ' + errorMessage);
    }
  };

  // 导入
  const handleImport = async (file: any) => {
    try {
      const result = await policyApi.import(file);
      message.success(result.data || '导入成功');
      loadData();
    } catch (error) {
      message.error('导入失败');
    }
    return false; // 阻止默认上传行为
  };

  // 下载模板
  const handleDownloadTemplate = async () => {
    try {
      const response = await policyApi.downloadTemplate();
      
      // 确保response.data是实际的文件数据
      // const blob = new Blob([response.data], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' });
      const blob = new Blob([response.data], { type: 'application/vnd.ms-excel' });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = '政策导航导入模板.xlsx';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
      
      message.success('模板下载成功');
    } catch (error) {
      console.error('模板下载失败:', error);
      message.error('模板下载失败');
    }
  };

  // 保存
  const handleSave = async () => {
    try {
      const values = await form.validateFields();
      const formData = {
        ...values,
        issuanceDate: values.issuanceDate ? values.issuanceDate.format('YYYY-MM-DD') : null,
      };
      
      
      if (editingItem) {
        const updateData = { 
          id: editingItem.id,
          ...formData 
        };
        await policyApi.update(updateData);
        message.success('修改成功');
      } else {
        await policyApi.create(formData);
        message.success('新增成功');
      }
      setModalVisible(false);
      loadData();
    } catch (error) {
      console.error('保存失败:', error);
      message.error('保存失败');
    }
  };

  // 表格列定义
  const columns: ColumnsType<PolicyItem> = [
    {
      title: '序号',
      key: 'index',
      width: 60,
      render: (_: any, __: any, index: number) => {
        // 动态生成序号：当前页 * 每页条数 + 当前行索引 + 1
        return (pagination.current - 1) * pagination.pageSize + index + 1;
      },
    },
    {
      title: '业务分类',
      dataIndex: 'businessCategory',
      key: 'businessCategory',
      width: 120,
      render: (businessCategory: string, record: PolicyItem) => {
        // 优先使用businessCategory，如果没有则使用category（兼容性字段）
        const categoryValue = businessCategory || record.category;
        const categoryItem = policyBusinessCategoryDict?.find(item => item.dictValue === categoryValue);
        
        return <Tag color="blue">{categoryItem?.dictLabel || categoryValue}</Tag>;
      },
    },
    {
      title: '文件名',
      dataIndex: 'fileName',
      key: 'fileName',
      width: 400,
      ellipsis: true,
    },
    {
      title: '文件号',
      dataIndex: 'documentNumber',
      key: 'documentNumber',
      width: 140,
      ellipsis: true,
    },
    {
      title: '印发时间',
      dataIndex: 'issuanceDate',
      key: 'issuanceDate',
      width: 100,
    },
    {
      title: '操作',
      key: 'action',
      width: 120,
      fixed: 'right',
      render: (_: any, record: PolicyItem) => (
        <Space size="small">
          <Button
            type="link"
            size="small"
            icon={<EditOutlined />}
            onClick={() => handleEdit(record)}
          >
            编辑
          </Button>
          <Popconfirm
            title="确定要删除吗？"
            onConfirm={() => handleDelete(record.id!)}
            okText="确定"
            cancelText="取消"
          >
            <Button type="link" size="small" danger icon={<DeleteOutlined />}>
              删除
            </Button>
          </Popconfirm>
        </Space>
      ),
    },
  ];

  return (
    <div className="policy-management">
      {/* 统一查询区域 */}
      <QueryArea
        fields={queryFields}
        values={searchParams}
        onValuesChange={handleSearchParamsChange}
        onAdd={handleAdd}
        onBatchDelete={handleBatchDelete}
        batchDeleteDisabled={selectedRowKeys.length === 0}
        selectedCount={selectedRowKeys.length}
        addButtonText="新增"
        batchDeleteButtonText="批量删除"
        customButtons={[
          {
            key: 'export',
            text: '导出',
            icon: <DownloadOutlined />,
            onClick: handleExport,
          },
          {
            key: 'import',
            text: '导入',
            icon: <UploadOutlined />,
            onClick: () => {
              const input = document.createElement('input');
              input.type = 'file';
              input.accept = '.xlsx,.xls';
              input.onchange = (e: any) => {
                const file = e.target.files[0];
                if (file) {
                  handleImport(file);
                }
              };
              input.click();
            },
          },
          {
            key: 'template',
            text: '下载模板',
            icon: <FileExcelOutlined />,
            onClick: handleDownloadTemplate,
          },
        ]}
      />

      {/* 数据表格 */}
      <div className="table-card">
        {dictLoading ? (
          <div style={{ textAlign: 'center', padding: '50px' }}>
            正在加载字典数据...
        </div>
        ) : (
        <Table
          columns={columns}
          dataSource={dataSource}
          loading={loading}
          rowKey="id"
          rowSelection={{
            selectedRowKeys,
            onChange: setSelectedRowKeys,
          }}
          pagination={{
            ...pagination,
            ...PAGINATION_CONFIG.default,
            onChange: (page, pageSize) => {
              setPagination(prev => ({ ...prev, current: page, pageSize: pageSize || 10 }));
            },
          }}
          scroll={{ x: 820 }}
        />
        )}
      </div>

      {/* 新增/编辑模态框 */}
      <Modal
        title={editingItem ? '编辑政策导航' : '新增政策导航'}
        open={modalVisible}
        onOk={handleSave}
        onCancel={() => setModalVisible(false)}
        width={800}
        okText="保存"
        cancelText="取消"
      >
        <Form form={form} layout="horizontal" labelCol={{ span: 4 }} wrapperCol={{ span: 20 }}>
          {/* 业务分类 - 单独一行 */}
          <Form.Item
            name="businessCategory"
            label="业务分类:"
            rules={[{ required: true, message: '请选择业务分类' }]}
            labelAlign="left"
          >
            <Select
              placeholder="请选择业务分类"
              loading={dictLoading}
              notFoundContent={dictLoading ? '加载中...' : '暂无数据'}
            >
              {policyBusinessCategoryDict?.map(item => (
                <Option key={item.dictValue} value={item.dictValue}>
                  {item.dictLabel}
                </Option>
              ))}
            </Select>
          </Form.Item>

          {/* 文件名 - 单独一行，因为内容较长 */}
          <Form.Item
            name="fileName"
            label="文件名:"
            rules={[{ required: true, message: '请输入文件名' }]}
            labelAlign="left"
          >
            <Input placeholder="请输入文件名" />
          </Form.Item>

          {/* 文号和印发时间 - 两个一行 */}
          <Row gutter={16}>
            <Col span={12}>
              <Form.Item
                name="documentNumber" 
                label="文号:"
                labelCol={{ span: 6 }}
                wrapperCol={{ span: 18 }}
                labelAlign="left"
              >
                <Input placeholder="请输入文号" />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item
                name="issuanceDate" 
                label="印发时间:"
                labelCol={{ span: 6 }}
                wrapperCol={{ span: 18 }}
                labelAlign="left"
              >
                <DatePicker
                  style={{ width: '100%' }}
                  locale={zhCN.DatePicker}
                  format="YYYY-MM-DD"
                />
              </Form.Item>
            </Col>
          </Row>

          {/* 发文机关和联合发文单位 - 两个一行 */}
          <Row gutter={16}>
            <Col span={12}>
              <Form.Item 
                name="issuingAuthority" 
                label="发文机关:"
                labelCol={{ span: 6 }}
                wrapperCol={{ span: 18 }}
                labelAlign="left"
              >
                <Input placeholder="请输入发文机关" />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item 
                name="jointIssuingUnit" 
                label="联合发文单位:"
                labelCol={{ span: 6 }}
                wrapperCol={{ span: 18 }}
                labelAlign="left"
              >
                <Input placeholder="请输入联合发文单位" />
              </Form.Item>
            </Col>
          </Row>

          {/* 文件下载地址 - 单独一行，因为内容较长 */}
          <Form.Item name="webLink" label="文件下载地址:" labelAlign="left">
            <Input placeholder="请输入文件下载地址" />
          </Form.Item>

          {/* 政策内容 - 单独一行，因为内容较多 */}
          <Form.Item name="content" label="政策内容:" labelAlign="left">
            <TextArea rows={4} placeholder="请输入政策内容" />
          </Form.Item>

          {/* 备注 - 单独一行 */}
          <Form.Item name="remark" label="备注:" labelAlign="left">
            <TextArea rows={2} placeholder="请输入备注" />
          </Form.Item>
        </Form>
      </Modal>
    </div>
  );
};

export default PolicyManagement;












